import { NavLink, useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useState } from 'react'
import {
  LayoutDashboard,
  Users,
  Clapperboard,
  Film,
  Megaphone,
  Inbox,
  LogOut,
  UserCircle,
  Menu,
  MessageSquare,
  Bell,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useMyApps } from '@/lib/myApps'
import { AccountHatLinks } from '@/components/AccountHatLinks'
import { useGuildRealtime } from '@/contexts/GuildRealtimeContext'
import { KinoraMark } from '@/components/KinoraMark'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { cn, initials } from '@/lib/utils'

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
  { label: 'Directory', path: '/directory', icon: Users },
  { label: 'Projects', path: '/projects', icon: Clapperboard },
  { label: 'Productions', path: '/productions', icon: Film },
  { label: 'Open Roles', path: '/roles', icon: Megaphone },
  { label: 'Applications', path: '/applications', icon: Inbox },
  { label: 'Messages', path: '/messages', icon: MessageSquare },
  { label: 'Notifications', path: '/notifications', icon: Bell },
]

function NavBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
      {count > 99 ? '99+' : count}
    </span>
  )
}

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { unreadMessages, unreadNotifications } = useGuildRealtime()
  const badges: Record<string, number> = {
    '/messages': unreadMessages,
    '/notifications': unreadNotifications,
  }
  return (
    <nav className="flex flex-col gap-1">
      {NAV_ITEMS.map(item => (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
            )
          }
        >
          <item.icon className="size-4" />
          {item.label}
          <NavBadge count={badges[item.path] ?? 0} />
        </NavLink>
      ))}
    </nav>
  )
}

function UserMenu() {
  const { guildProfile, user, signOut } = useAuth()
  const navigate = useNavigate()
  const name = guildProfile?.display_name || user?.email || 'Member'
  const apps = useMyApps(user?.id)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-sidebar-accent/60">
          <Avatar className="size-8">
            <AvatarImage src={guildProfile?.avatar_url ?? undefined} alt={name} />
            <AvatarFallback>{initials(name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-sidebar-foreground">{name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {guildProfile?.headline || user?.email}
            </p>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel>Guild member</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate('/profile')}>
          <UserCircle className="size-4" />
          My Profile
        </DropdownMenuItem>
        <AccountHatLinks apps={apps} currentApp="guild" />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          onClick={async () => {
            await signOut()
            navigate('/auth')
          }}
        >
          <LogOut className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const apps = useMyApps(user?.id)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col border-r border-sidebar-border bg-sidebar px-3 py-4 md:flex">
        <NavLink to="/dashboard" className="mb-6 flex items-center gap-2.5 px-3">
          <KinoraMark size={26} glow />
          <span className="font-display text-lg font-semibold tracking-tight text-sidebar-foreground">
            Kinora <span className="text-primary">Guild</span>
          </span>
        </NavLink>
        <NavItems />
        <div className="mt-auto border-t border-sidebar-border pt-3">
          <UserMenu />
        </div>
      </aside>

      {/* Mobile header */}
      <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between border-b border-sidebar-border bg-sidebar px-4 md:hidden">
        <NavLink to="/dashboard" className="flex items-center gap-2">
          <KinoraMark size={22} />
          <span className="font-display text-base font-semibold text-sidebar-foreground">
            Kinora <span className="text-primary">Guild</span>
          </span>
        </NavLink>
        <DropdownMenu open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {NAV_ITEMS.map(item => (
              <DropdownMenuItem key={item.path} asChild>
                <NavLink to={item.path} onClick={() => setMobileNavOpen(false)}>
                  <item.icon className="size-4" />
                  {item.label}
                </NavLink>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <NavLink to="/profile" onClick={() => setMobileNavOpen(false)}>
                <UserCircle className="size-4" />
                My Profile
              </NavLink>
            </DropdownMenuItem>
            <AccountHatLinks
              apps={apps}
              currentApp="guild"
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <main className="min-w-0 flex-1 pt-14 md:ml-60 md:pt-0">
        <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-8 md:py-8">
          {children}
        </div>
      </main>
    </div>
  )
}
